/**
 * NotificationCenter — sidebar popover showing latest notifications.
 *
 * Design: Warm Minimal (Scholarly Modernist).
 * - Paper white surfaces (--background), ink text (--foreground)
 * - Libre Bodoni display headings, Public Sans body
 * - Unread items: warm terracotta left-border accent (4px) + bold title
 * - Read items: muted title, no accent
 * - "Mark all read" with terracotta text
 * - Empty + error + skeleton states
 * - Dividers between items (no card nesting)
 *
 * Layout: 320px wide, max-height 420px with overflow scroll.
 * RTL-aware: text direction and spacing via logical properties.
 */
import { Bell, CheckCheck } from "lucide-react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { cn, formatNum, formatDate } from "@/shared/lib/utils";
import { useLocale } from "@/shared/hooks/useLocale";
import { getEffectiveDir } from "@/shared/lib/direction";
import {
  useMarkAllRead,
  useMarkRead,
  useNotificationCenter,
} from "../hooks/useNotifications";
import { FcmSubscribePrompt } from "./FcmSubscribePrompt";
import type { Notification } from "../api/notificationApi";

/* ── Time formatting ─────────────────────────────────────────────────────────── */

// numLocale carries the digit choice (`-u-nu-` extension) so digits follow the
// user's numberFormat preference; `t` (present only in Arabic) supplies the
// localized unit words. Digits and words are thus independent concerns.
function timeAgo(dateString: string, numLocale: string, t?: (key: string) => string): string {
  if (!dateString) return "—";
  const now = Date.now();
  const then = new Date(dateString).getTime();
  if (isNaN(then)) return "—";
  const diff = Math.floor((now - then) / 1000);

  const fmt = (n: number) => formatNum(n, numLocale);

  if (diff < 60) return `${fmt(diff)} ${t ? t("timeAgo.s") : "s"}`;
  if (diff < 3600) return `${fmt(Math.floor(diff / 60))} ${t ? t("timeAgo.m") : "m"}`;
  if (diff < 86400) return `${fmt(Math.floor(diff / 3600))} ${t ? t("timeAgo.h") : "h"}`;
  if (diff < 604800) return `${fmt(Math.floor(diff / 86400))} ${t ? t("timeAgo.d") : "d"}`;
  return formatDate(dateString, numLocale, { dateStyle: "short" });
}

/* ── Sub-components ─────────────────────────────────────────────────────────── */

function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onNavigate?: () => void;
}) {
  const { t, i18n } = useTranslation("notifications");
  const { numLocale } = useLocale();
  const isRTL = i18n.language === "ar";
  const effectiveDir = getEffectiveDir();

  const formatTime = (dateString: string) => timeAgo(dateString, numLocale, isRTL ? t : undefined);

  const handleActivate = () => {
    if (!notification.isRead) onMarkRead(notification.id);
    onNavigate?.();
  };

  const inner = (
    <div
      className={cn(
        "group relative flex gap-3 px-4 py-3.5 transition-colors",
        "hover:bg-[var(--color-surface-low)]",
        notification.isRead
          ? ""
          : "bg-[oklch(0.984_0.003_80_/_0.35)]"
      )}
      style={{
        borderBottom: "1px solid var(--border)",
      }}
    >
      {/* Unread terracotta bar — left in LTR, right in RTL */}
      {!notification.isRead && (
        <span
          className={cn(
            "absolute top-0 bottom-0 w-0.5 rounded-full shrink-0",
            isRTL ? "end-0" : "start-0"
          )}
          style={{ background: "oklch(0.627 0.168 81.944)" }}
          aria-hidden
        />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0 ps-3">
        <p
          className="text-sm leading-snug text-[var(--foreground)] font-semibold"
          dir={effectiveDir}
          style={
            !notification.isRead
              ? { fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }
              : undefined
          }
        >
          {!notification.isRead && (
            <span
              className="inline-block w-2 h-2 rounded-full me-1.5 align-middle"
              // --color-error is mode-aware (brighter red in dark) so the unread
              // marker stays visible on both light and dark surfaces, unlike the
              // flat --sidebar-danger which is near-invisible on the dark card.
              style={{ background: "var(--color-error)" }}
              aria-hidden
            />
          )}
          {isRTL ? notification.titleAr : notification.titleEn}
        </p>
        {(isRTL ? notification.bodyAr : notification.bodyEn) && (
          <p
            className="text-[11px] text-[var(--muted-foreground)] mt-0.5 leading-snug line-clamp-2"
            dir={effectiveDir}
          >
            {isRTL ? notification.bodyAr : notification.bodyEn}
          </p>
        )}
        <p className="text-[10px] text-[var(--muted-foreground)] mt-1 tabular-nums" dir={effectiveDir}>
          {formatTime(notification.createdAt)}
        </p>
      </div>

      {/* Mark-read action — appears on hover, only for unread */}
      {!notification.isRead && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMarkRead(notification.id);
          }}
          aria-label={t("markRead", "Mark as read")}
          title={t("markRead", "Mark as read")}
          className={cn(
            "shrink-0 flex items-center justify-center w-7 h-7 rounded-md self-center",
            "transition-colors duration-150",
            "text-[var(--muted-foreground)] hover:text-[var(--foreground)]",
            "hover:bg-[var(--color-surface-low)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          )}
        >
          <CheckCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );

  if (!notification.url) return inner;

  return (
    <Link to={notification.url} onClick={handleActivate} className="block">
      {inner}
    </Link>
  );
}

function NotificationSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-[var(--border)]">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex gap-3 px-4 py-3.5">
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-[var(--color-surface-low)] rounded-full w-4/5 animate-pulse" />
            <div className="h-3 bg-[var(--color-surface-low)] rounded-full w-2/5 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NotificationEmpty() {
  const { t } = useTranslation("notifications");
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
        style={{ background: "var(--color-surface-low)", borderRadius: "var(--radius)" }}
      >
        <Bell
          className="w-5 h-5"
          strokeWidth={1.5}
          style={{ color: "var(--muted-foreground)" }}
        />
      </div>
      <p
        className="text-sm font-semibold"
        style={{
          fontFamily: "var(--font-display)",
          letterSpacing: "-0.01em",
          color: "var(--foreground)",
        }}
      >
        {t("emptyTitle", "No notifications yet")}
      </p>
      <p className="text-xs mt-1" style={{ color: "var(--muted-foreground)" }}>
        {t(
          "notifications.emptyDesc",
          "You're all caught up. New activity will appear here."
        )}
      </p>
    </div>
  );
}

function NotificationError() {
  const { t } = useTranslation("notifications");
  return (
    <div className="flex items-center gap-2 px-4 py-3.5">
      <p className="text-xs" style={{ color: "var(--destructive)" }}>
        {t("loadError")}
      </p>
    </div>
  );
}

interface NotificationCenterProps {
  onNavigate?: () => void;
}

/* ── Main component ─────────────────────────────────────────────────────────── */

export function NotificationCenter({ onNavigate }: NotificationCenterProps = {}) {
  const { t } = useTranslation("notifications");
  const { data, isLoading, isError } = useNotificationCenter();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;
  const hasUnread = unreadCount > 0;

  return (
    <div
      className="flex flex-col"
      style={{
        maxHeight: 420,
        background: "var(--card)",
        borderRadius: "var(--radius-xl)",
        overflow: "hidden",
        border: "1px solid var(--border)",
        boxShadow: "var(--shadow-warm)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 shrink-0"
        style={{
          borderBottom: "1px solid var(--border)",
        }}
      >
        {/* Title with terracotta underline accent */}
        <div className="flex items-center gap-2">
          <span
            className="block w-1 h-4 rounded-full"
            style={{ background: "oklch(0.627 0.168 81.944)" }}
            aria-hidden
          />
          <p
            className="text-sm font-semibold"
            style={{
              fontFamily: "var(--font-display)",
              letterSpacing: "-0.01em",
              color: "var(--foreground)",
            }}
          >
            {t("pageTitle")}
          </p>
        </div>

        {/* Unread count pill + mark-all action */}
        {hasUnread && (
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className={cn(
              "flex items-center gap-1 text-[11px] transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            style={{ color: "var(--sidebar-danger)" }}
          >
            <CheckCheck className="w-3 h-3" strokeWidth={2} />
            {t("markAllRead", "Mark all read")}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1">
        <FcmSubscribePrompt />
        {isLoading && <NotificationSkeleton />}
        {isError && <NotificationError />}
        {!isLoading && !isError && notifications.length === 0 && (
          <NotificationEmpty />
        )}
        {!isLoading && !isError && notifications.length > 0 && (
          <div>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={(id) => markRead.mutate([id])}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div
          className="shrink-0 px-4 py-2.5 text-center"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <button
            type="button"
            onClick={() => { onNavigate?.(); }}
            className="text-[11px] transition-colors hover:underline"
            style={{ color: "var(--muted-foreground)" }}
          >
            {t("viewAll")}
          </button>
        </div>
      )}

          </div>
  );
}
